
import { mixins } from 'vue-class-component';
import { ViewModeMixin } from '@/utils/viewMode.mixin';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Citation } from '@/store/modules/documents/types/citations.types';
import {
  PendingDocumentCitationReference,
  PendingDocumentReferences,
  PendingDocumentReferenceUpdate,
  PendingCitationData
} from '@/jbi-shared/types/document.types';
import {
  getCitationReferenceNumber,
  transformAuthorArrayToString
} from '@/utils/citation-reference.util';
import StagingTextSectionCitationDialog from './StagingTextSectionCitationDialog.vue';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { CitationTypes } from '@/jbi-shared/types/citation.fields';
import Cite from 'citation-js';
import { getDatePartsFromIssueDate } from '../../../../jbi-shared/util/citation.util';
import { StagingEditorComputedValuesMixin } from '../../mixins/staging-editor-computed-values.mixin';
import { PendingCitationResponse } from '@/api/pending-citations.api';

@Component({})
export default class StagingTextSectionCitation extends mixins(
  ViewModeMixin,
  StagingEditorComputedValuesMixin
) {
  @Prop()
  public currentCitation!: PendingDocumentCitationReference;
  @Prop()
  public previousCitations!: Citation[];

  get storeNewlyAddedCitations(): PendingCitationResponse[] {
    if (!this.documentDetail?.id) {
      return [];
    }
    // Force reactivity by accessing lastUpdated
    const forceReactivity = this.$store.state.pendingCitations.lastUpdated;

    const citations = this.$store.getters[
      'pendingCitations/getCitationsForDocument'
    ](this.documentDetail.id).filter(
      (citation: PendingCitationResponse) => citation.status === 'ACTIVE'
    );

    return citations;
  }

  get allCitations(): Citation[] {
    // Force reactivity by accessing lastUpdated
    const forceReactivity = this.$store.state.pendingCitations.lastUpdated;

    // Use the same logic as updatedCitations from the mixin to ensure consistency
    const citationMap = new Map<string, Citation>();

    const previousCitations = this.previousCitations || [];
    previousCitations.forEach((citation) => {
      citationMap.set(citation.id.toString(), citation);
    });

    // Using activeCitations instead of dirtyCitations as it contains the most up-to-date data
    // This includes both uploaded citations AND edited previous citations
    const activeCitationsSource = this.activeCitations || [];
    const activeCitationsConverted = activeCitationsSource.map((citation) =>
      this.convertPendingCitationToDisplayFormat(citation)
    );
    activeCitationsConverted.forEach((citation) => {
      citationMap.set(citation.id.toString(), citation);
    });

    // Overwrite with newly added citations from store (most up-to-date)
    const newlyAddedCitations = this.storeNewlyAddedCitations.map((citation) =>
      this.convertNewlyAddedToDisplayFormat(citation)
    );
    newlyAddedCitations.forEach((citation) => {
      citationMap.set(citation.id.toString(), citation);
    });

    return Array.from(citationMap.values());
  }

  get newlyAddedCitations() {
    // Force reactivity by accessing lastUpdated
    const forceReactivity = this.$store.state.pendingCitations.lastUpdated;

    const citations = this.storeNewlyAddedCitations.map((citation) =>
      this.convertNewlyAddedToDisplayFormat(citation)
    );
    return citations;
  }

  get uploadedCitations() {
    // Force reactivity by accessing lastUpdated
    const forceReactivity = this.$store.state.pendingCitations.lastUpdated;

    const newlyAddedIds = new Set(
      this.storeNewlyAddedCitations.map((citation) => citation.citationId)
    );
    const previousCitationIds = new Set(
      this.previousCitations.map((citation) => Number(citation.id))
    );

    // Use activeCitations instead of dirtyCitations as it contains the most up-to-date data
    // This matches the logic in StagingEditorCitationsTab.vue
    const activeCitationsSource = this.activeCitations || [];

    const filtered = activeCitationsSource
      .filter((activeCitation) => {
        const citationId = Number(activeCitation.id);
        const isNewlyAdded = newlyAddedIds.has(citationId);
        const isPrevious = previousCitationIds.has(citationId);
        return !isNewlyAdded && !isPrevious;
      })
      .map((citation) => this.convertPendingCitationToDisplayFormat(citation));

    return filtered;
  }

  get updatedPreviousCitations() {
    // Force reactivity by accessing lastUpdated
    const forceReactivity = this.$store.state.pendingCitations.lastUpdated;

    const previousCitations = this.previousCitations || [];
    const activeCitationsSource = this.activeCitations || [];

    // Create a map of active citations for quick lookup
    const activeCitationsMap = new Map();
    activeCitationsSource.forEach((citation) => {
      activeCitationsMap.set(citation.id.toString(), citation);
    });

    // For each previous citation, use the updated version from activeCitations if it exists
    return previousCitations.map((citation) => {
      const updatedCitation = activeCitationsMap.get(citation.id.toString());
      if (updatedCitation) {
        // Use the updated version from activeCitations
        return this.convertPendingCitationToDisplayFormat(updatedCitation);
      }
      // Use the original citation
      return citation;
    });
  }

  convertNewlyAddedToDisplayFormat(
    citation: PendingCitationResponse
  ): Citation {
    return {
      id: citation.citationId, // Keep as number to match Citation type
      content: {
        ...citation.citation.content,
        author: transformAuthorArrayToString(citation.citation.content.author)
      },
      createdAt: citation.citation.createdAt
    } as Citation;
  }

  convertPendingCitationToDisplayFormat(
    citation: PendingCitationData
  ): Citation {
    return {
      id: citation.id,
      content: {
        ...citation.content,
        author: transformAuthorArrayToString(citation.content.author)
      }
    } as Citation;
  }

  transformAuthorArrayToString(
    authors:
      | Array<{ given?: string; family?: string }>
      | string
      | null
      | undefined
  ): string {
    return transformAuthorArrayToString(authors);
  }

  public selectedCitation: string = '';

  get proxySelectedCitation() {
    if (this.selectedCitation === '') {
      this.selectedCitation = this.currentCitation.citationId;
    }
    return this.selectedCitation;
  }
  set proxySelectedCitation(value) {
    this.selectedCitation = value;
  }

  @isDifferent
  @isTruthy
  @Watch('proxySelectedCitation')
  public onSelectedChange(value: string) {
    this.proxySelectedCitation = value;
  }

  public citationReferenceNumber(citation: Citation): number | undefined {
    return getCitationReferenceNumber(
      citation,
      this.dirtyReferences?.citationIds || []
    );
  }

  public citationText(citation: Citation): string {
    if (citation.content.type === CitationTypes.PLAIN) {
      return citation.content.plainText;
    } else {
      // for displaying the right reference format in manage modal
      const citationContent = { ...citation.content };
      if (typeof citationContent.author === 'string') {
        // Check if it's a newly added citation
        const originalNewlyAddedCitation = this.storeNewlyAddedCitations.find(
          (c) => c.citationId === citation.id
        );
        if (originalNewlyAddedCitation) {
          citationContent.author =
            originalNewlyAddedCitation.citation.content.author;
        } else {
          const originalUploadedCitation = this.activeCitations?.find(
            (c) => c.id === citation.id
          );
          if (originalUploadedCitation) {
            citationContent.author = originalUploadedCitation.content.author;
          }
        }
      }

      const rawCitationObject = new Cite(citationContent);

      // Special Fix for Vancouver format to display date, and support multiple types of formats
      rawCitationObject.data[0].issued = getDatePartsFromIssueDate(
        rawCitationObject?.data[0]?.issued
      );

      const formattedCitation = rawCitationObject.format('bibliography', {
        template: 'vancouver'
      });
      return formattedCitation.trim().substr(3);
    }
  }

  public saveCitation() {
    if (this.proxySelectedCitation === this.currentCitation.citationId) {
      this.$emit('close');
    } else {
      const newCitation = this.allCitations.find(
        (citation) => String(citation.id) === String(this.proxySelectedCitation)
      );
      if (newCitation) {
        const referenceNumber = getCitationReferenceNumber(
          newCitation!,
          this.dirtyReferences?.citationIds || []
        );
        if (!referenceNumber) {
          // New citation selected is not in used
          const payload: PendingDocumentReferenceUpdate = {
            mode: 'replace',
            label: this.currentCitation.label,
            previousCitationId: this.currentCitation.citationId,
            newCitationId: this.proxySelectedCitation
          };
          this.updateReferenceCitation(payload);
        } else {
          // New citation selected is in used, prompt dialog to replace/merge citation
          this.$buefy.modal.open({
            parent: this,
            component: StagingTextSectionCitationDialog,
            hasModalCard: true,
            trapFocus: true,
            props: {
              referenceNumber: getCitationReferenceNumber(
                newCitation!,
                this.dirtyReferences?.citationIds || []
              )
            },
            events: {
              'update:referenceCitation': (
                mode: PendingDocumentReferenceUpdate['mode']
              ) => {
                const payload: PendingDocumentReferenceUpdate = {
                  mode,
                  label: this.currentCitation.label,
                  previousCitationId: this.currentCitation.citationId,
                  newCitationId: this.proxySelectedCitation
                };
                this.updateReferenceCitation(payload);
              }
            }
          });
        }
      }
    }
  }

  public updateReferenceCitation(
    referenceUpdatePayload: PendingDocumentReferenceUpdate
  ) {
    this.$emit('updateReferenceCitation', referenceUpdatePayload);
    this.$emit('close');
  }
}
